import "./App.css";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect, useMemo, useRef } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { handleAuthError, isAuthError } from "./utils/handleAuthError";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { onAuthStateChanged } from "firebase/auth";
import { Unsubscribe } from "firebase/firestore";
import { auth } from "./firebase";
import { suscribirAgencia } from "./firebase/firestore/agencia";
import HomeRoute from "./screens/HomeRoute";
import ProtectedRoute from "./screens/ProtectedRoute";
import RoleProtectedRoute from "./screens/RoleProtectedRoute";
import LoginRoute from "./screens/auth/LoginRoute";
import RegisterRoute from "./screens/auth/RegisterRoute";
import ResetPasswordScreen from "./screens/auth/ResetPasswordRoute";
import PrivacyPolicyScreen from "./screens/PrivacyPolicy";
import DeleteAccountRoute from "./screens/DeleteAccountRoute";
import SupportRoute from "./screens/SupportRoute";
import { useAgenciaStore } from "./zustand/useAgenciaStore";
import { useAuthStore } from "./zustand/useAuthStore";
import { useThemeStore } from "./zustand/useThemeStore";
import { useSystemTheme } from "./hooks/useSystemTheme";
import { createAppTheme } from "./theme";
import UsersRoute from "./screens/UsersRoute";
import ReferidosRoute from "./screens/ReferidosRoute";
import AnalyticsRoute from "./screens/AnalyticsRoute";
import ShipmentsRoute from "./screens/ShipmentsRoute";
import PromotionsRoute from "./screens/PromotionsRoute";
import SettingsRoute from "./screens/SettingsRoute";
import { UserRole } from "./interfaces/UserRole";

const LOGIN_PATH = "/login";

const PUBLIC_PATHS = ["/login", "/register", "/reset-password", "/privacy-policy", "/delete-account", "/support"];

function isPublicPath(pathname: string) {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function App() {
  const { setAgencia, setLoading, clearAgencia } = useAgenciaStore();
  useAuthStore();
  const { mode } = useThemeStore();
  const systemTheme = useSystemTheme();

  const effectiveMode = mode === "system" ? systemTheme : mode;
  const theme = useMemo(() => createAppTheme(effectiveMode), [effectiveMode]);

  const agenciaUnsubRef = useRef<Unsubscribe | null>(null);

  // Escuchar agencia en tiempo real solo si hay sesión válida; si no, cortar el
  // listener y redirigir a login (salvo en rutas públicas). El listener permite
  // que un cambio de `activo` en Firestore suspenda o reanude el servicio sin recargar.
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        // Desuscribir antes de cualquier redirección: un listener vivo sin sesión
        // dispara permission-denied.
        agenciaUnsubRef.current?.();
        agenciaUnsubRef.current = null;
        clearAgencia();
        if (!isPublicPath(window.location.pathname)) {
          window.location.href = LOGIN_PATH;
        }
        return;
      }
      if (agenciaUnsubRef.current) return;
      agenciaUnsubRef.current = suscribirAgencia(setAgencia, () =>
        setLoading(false)
      );
    });

    return () => {
      unsubscribeAuth();
      agenciaUnsubRef.current?.();
      agenciaUnsubRef.current = null;
    };
  }, [setAgencia, setLoading, clearAgencia]);

  // Ante 403 / permisos / no autenticado en cualquier parte, cerrar sesión y redirigir a login
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isAuthError(event.reason)) {
        event.preventDefault();
        handleAuthError();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }, []);


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <BrowserRouter>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={effectiveMode}
          />
          <Routes>
            <Route path="/register" element={<RegisterRoute />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/delete-account" element={<DeleteAccountRoute />} />
            <Route path="/support" element={<SupportRoute />} />
            <Route element={<ProtectedRoute />}>
              {/* Rutas accesibles para Digitador y Admin */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/shipments" element={<ShipmentsRoute />} />

              {/* Rutas solo para Admin */}
              <Route
                element={<RoleProtectedRoute allowedRoles={[UserRole.Admin]} />}
              >
                <Route path="/users" element={<UsersRoute />} />
                <Route path="/referidos" element={<ReferidosRoute />} />
                <Route path="/analytics" element={<AnalyticsRoute />} />
                <Route path="/promotions" element={<PromotionsRoute />} />
                <Route path="/settings" element={<SettingsRoute />} />
              </Route>

              {/* Perfil y configuración: misma pantalla para todos los roles autenticados */}
              <Route path="/profile" element={<SettingsRoute />} />
            </Route>
            <Route path="*" element={<h1>404 - Página no encontrada</h1>} />
          </Routes>
        </BrowserRouter>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
